import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { ArrowLeft, CircleCheck, Circle, } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import Cookies from "js-cookie";

const CONFIRMATION_OPTIONS = [
  { key: "auto", titleKey: "AutoConfirmTitle", descKey: "AutoConfirmDesc", icon: "/icons/auto.svg" },
  { key: "manual", titleKey: "ManualConfirmTitle", descKey: "ManualConfirmDesc", icon: "/icons/manual.svg" },
];

export const AUTOMATIC_OPTIONS = [
  { key: "addOnServicesSelected", titleKey: "addOnServicesSelected" },
  { key: "specialCareRequirements", titleKey: "specialCareRequirements" },
  { key: "petStayingTogether", titleKey: "petStayingTogether" },
  { key: "LateCheckInCheckOut", titleKey: "LateCheckInCheckOut" },
  { key: "petDetailsNotMeetRoomRequirements", titleKey: "petDetailsNotMeetRoomRequirements" },
];

const HotelAdd_YSubmit = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:850px)");
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const [confirmationMethod, setConfirmationMethod] = useState("");
  const [selectedAutoOptions, setSelectedAutoOptions] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchDraft, hotelData, submit, saveStep } = useHotel();

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (hotelData.bookingConfirmationMethod) {
      setConfirmationMethod(hotelData.bookingConfirmationMethod.automatic ? "auto" : "manual");
      const requireManualConfirmation = hotelData.bookingConfirmationMethod.requireManualConfirmation;
      if (Array.isArray(requireManualConfirmation) && requireManualConfirmation.length > 0) {
        const selectedKeys = [];
        const options = requireManualConfirmation[0];
        if (options.addOnServicesSelected) selectedKeys.push("addOnServicesSelected");
        if (options.specialCareRequirements) selectedKeys.push("specialCareRequirements");
        if (options.petStayingTogether) selectedKeys.push("petStayingTogether");
        if (options.LateCheckInCheckOut) selectedKeys.push("LateCheckInCheckOut");
        if (options.petDetailsNotMeetRoomRequirements) selectedKeys.push("petDetailsNotMeetRoomRequirements");
        setSelectedAutoOptions(selectedKeys);
      }
    }
  }, [hotelData.bookingConfirmationMethod]);

  const isNextDisabled = !confirmationMethod || isSubmitting;

  const toggleAutoOption = (optionKey) => {
    setSelectedAutoOptions((prev) =>
      prev.includes(optionKey)
        ? prev.filter((key) => key !== optionKey)
        : [...prev, optionKey]
    );
  };

  const handleSubmit = async () => {
    try {
      if (!confirmationMethod) return;

      setIsSubmitting(true);
      let bookingConfirmationMethod = {};
      if (confirmationMethod === "manual") {
        bookingConfirmationMethod = { "manual": true };
      }
      else {
        bookingConfirmationMethod = {
          "automatic": true,
          "requireManualConfirmation": [
            {
              "addOnServicesSelected": Boolean(selectedAutoOptions.includes("addOnServicesSelected")),
              "specialCareRequirements": Boolean(selectedAutoOptions.includes("specialCareRequirements")),
              "petStayingTogether": Boolean(selectedAutoOptions.includes("petStayingTogether")),
              "LateCheckInCheckOut": Boolean(selectedAutoOptions.includes("LateCheckInCheckOut")),
              "petDetailsNotMeetRoomRequirements": Boolean(selectedAutoOptions.includes("petDetailsNotMeetRoomRequirements")),
            }
          ]
        };
      }
      await saveStep("bookingConfirmationMethod", bookingConfirmationMethod);
      if (edit) {
        navigate(`/hotel/${id}/information`);
      }
      else {
        const isPublishedBody = {
          "isPublished": true
        };
        await saveStep("isPublished", isPublishedBody);
        await submit(id);
        Cookies.remove("hotelAccountNumber");
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error submitting hotel:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      if (!confirmationMethod) return;

      let bookingConfirmationMethod = {};
      if (confirmationMethod === "manual") {
        bookingConfirmationMethod = { "manual": true };
      }
      else {
        bookingConfirmationMethod = {
          "automatic": true,
          "requireManualConfirmation": [
            {
              "addOnServicesSelected": Boolean(selectedAutoOptions.includes("addOnServicesSelected")),
              "specialCareRequirements": Boolean(selectedAutoOptions.includes("specialCareRequirements")),
              "petStayingTogether": Boolean(selectedAutoOptions.includes("petStayingTogether")),
              "LateCheckInCheckOut": Boolean(selectedAutoOptions.includes("LateCheckInCheckOut")),
              "petDetailsNotMeetRoomRequirements": Boolean(selectedAutoOptions.includes("petDetailsNotMeetRoomRequirements")),
            }
          ]
        };
      }

      await saveStep("bookingConfirmationMethod", bookingConfirmationMethod);
    } catch (error) {
      console.error("Error submitting hotel:", error);
    }
  };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                minWidth: "auto",
                width: isMobile ? "36px" : "44px",
                height: isMobile ? "36px" : "44px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                color: "#fff",
                padding: 0,
                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
              }}
            >
              <ArrowLeft size={isMobile ? 18 : 22} />
            </Button>
            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
              {t('LastStepLabel')}
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "18px" : "20px", color: "#000", mb: 1 }}>
            {t('SelectConfirmationMethodTitle')}
          </Typography>
          <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666", mb: 3 }}>
            {t('SelectConfirmationMethodDesc')}
          </Typography>

          {confirmationMethod === "auto" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                backgroundColor: "#FFF8E1",
                border: "1px solid #FFE082",
                borderRadius: "10px",
                padding: isMobile ? "12px 16px" : "14px 20px",
                mb: 2,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box component="img" src="/icons/alert.svg" alt="" sx={{ width: 20, height: 20, flexShrink: 0, mt: 0.5 }} />
              <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#8D6E00" }}>
                {t('AutoConfirmWarning')}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            {CONFIRMATION_OPTIONS.map((option) => {
              const isSelected = confirmationMethod === option.key;
              return (
                <>
                  <Box
                    key={option.key}
                    onClick={() => setConfirmationMethod(option.key)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      backgroundColor: "#FFFFFF",
                      border: isSelected ? "1.5px solid #31BACD" : "1.5px solid #D1D1D1",
                      borderRadius: "10px",
                      padding: isMobile ? "16px" : "20px 24px",
                      cursor: "pointer",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {isSelected ? (
                      <CircleCheck size={20} color="#31BACD" style={{ flexShrink: 0, marginTop: 2 }} />
                    ) : (
                      <Circle size={20} color="#D1D1D1" style={{ flexShrink: 0, marginTop: 2 }} />
                    )}
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                          {t(option.titleKey)}
                        </Typography>
                        <Box component="img" src={option.icon} alt="" sx={{ width: 20, height: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666" }}>
                        {t(option.descKey)}
                      </Typography>
                    </Box>
                  </Box>
                  {isSelected && option.key === "auto" && (
                    <Box sx={{ padding: "16px", width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                          {t("SpecialCircumstancesTitle")}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666", mb: 2 }}>
                        {t("SpecialCircumstancesDesc")}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {AUTOMATIC_OPTIONS.map((autoOption) => {
                          const isAutoSelected = selectedAutoOptions.includes(autoOption.key);
                          return (
                            <Box
                              key={autoOption.key}
                              onClick={() => toggleAutoOption(autoOption.key)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                cursor: "pointer",
                                padding: "8px",
                                borderRadius: "6px",
                                transition: "background-color 0.2s",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                              }}
                            >
                              {isAutoSelected ? (
                                <CircleCheck size={16} color="#31BACD" style={{ flexShrink: 0 }} />
                              ) : (
                                <Circle size={16} color="#999" style={{ flexShrink: 0 }} />
                              )}
                              <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666" }}>
                                {t(autoOption.titleKey)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </>
              );
            })}
          </Box>

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSubmitting}
                onClick={handleSubmit}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t('Submit')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />

      {showSuccessModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 2 : 4,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: isMobile ? "24px" : "32px 40px",
              width: isMobile ? "90%" : "auto",
              maxWidth: "700px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0 }}>
              <Box
                component="img"
                src="/icons/ppclogo.png"
                alt="Pet Pet Club"
                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box component="img" src="/icons/success.svg" alt="" sx={{ width: 72, height: 72 }} />
              </Box>
              <Typography sx={{ fontSize: isMobile ? "11px" : "12px", color: "#666", mb: 3 }}>
                {t('SubmitSuccessDesc')}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/hotel/onboarding")}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "13px" : "14px",
                  padding: "8px 0",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                }}
              >
                {t('BackToHomepage')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_YSubmit;
